/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * Types of Wizard
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.2.0
 */

import { SagaIterator } from 'redux-saga';

import { EWizardCollationReportSteps } from '@/main/pages/WizardCollationReport/wizardCollationReportConstant';
import { wizardCollationReportSagas } from '@/main/pages/WizardCollationReport/wizardCollationReportSagas';
import { EWizardImportSubscribersLimitsSteps } from '@/main/pages/WizardImportSubscribersLimits/wizardImportSubscribersLimitsConstant';
import { wizardImportSubscribersLimitsSagas } from '@/main/pages/WizardImportSubscribersLimits/wizardImportSubscribersLimitsSagas';
import { EMoneyTransferSteps } from '@pages/MoneyTransfer/moneyTransferConstants';
import { moneyTransferSagas } from '@pages/MoneyTransfer/moneyTransferSagas';
import { ELabelsImportSteps } from '@pages/PageLabelsImport/labelsImportConstants';
import { labelsImportSagas } from '@pages/PageLabelsImport/labelsImportSagas';
import { EWizardAdditionalAccountsSteps } from '@pages/WizardAdditionalAccounts/wizardAdditionalAccountsConstant';
import { wizardAdditionalAccountsSagas } from '@pages/WizardAdditionalAccounts/wizardAdditionalAccountsSagas';
import { EWizardAdditionalReportsSteps } from '@pages/WizardAdditionalReports/wizardAdditionalReportsConsant';
import { wizardAdditionalReportsSagas } from '@pages/WizardAdditionalReports/wizardAdditionalReportsSagas';
import { EWizardBillsDocSteps } from '@pages/WizardBillsDoc/wizardBillsDocConsant';
import { wizardBillsDocSagas } from '@pages/WizardBillsDoc/wizardBillsDocSagas';
import { ECallsDetailSteps } from '@pages/WizardCallsDetail/callsDetailConstants';
import { callsDetailSagas } from '@pages/WizardCallsDetail/callsDetailSagas';
import { EWizardChangeLockSteps } from '@pages/WizardChangeLock/wizardChangeLockConstant';
import { wizardChangeLockSagas } from '@pages/WizardChangeLock/wizardChangeLockSagas';
import { EWizardChangeRoleSteps } from '@pages/WizardChangeRole/wizardChangeRoleConstant';
import { wizardChangeRoleSagas } from '@pages/WizardChangeRole/wizardChangeRoleSagas';
import { EWizardExpensesDetailSteps } from '@pages/WizardExpensesDetail/wizardExpensesDetailConstant';
import { wizardExpensesDetailSagas } from '@pages/WizardExpensesDetail/wizardExpensesDetailSagas';
import { EWizardImportPersonalDataSteps } from '@pages/WizardImportPersonalData/wizardImportPersonalDataConstant';
import { wizardImportPersonalDataSagas } from '@pages/WizardImportPersonalData/wizardImportPersonalDataSagas';
import { EWizardMultiSearchSteps } from '@pages/WizardMultiSearch/constant';
import { wizardMultiSearchSagas } from '@pages/WizardMultiSearch/saga';
import { EWizardProductOfferingsSteps } from '@pages/WizardProductOfferings/wizardProductOfferingsConstants';
import { wizardProductOfferingsSagas } from '@pages/WizardProductOfferings/wizardProductOfferingsSagas';
import { EWizardOrderReportSteps } from '@pages/WizardReportsConstructor/WizardOrderReport/constants';
import { wizardOrderReportSagas } from '@pages/WizardReportsConstructor/WizardOrderReport/saga';
import { EWizardOrderTemplateSteps } from '@pages/WizardReportsConstructor/WizardOrderTemplate/constants';
import { wizardOrderTemplateSagas } from '@pages/WizardReportsConstructor/WizardOrderTemplate/saga';

import { EBasketType } from '../app/appConstants';
import { IAction, IActionWithOptionalPayload, IActionWithPayload } from '../app/createAction';
import { EWizardReportParam } from './components/WizardReport/WizardReportConstants';
import { IWizardReportParam } from './components/WizardReport/wizardReportTypes';
import {
    EWizardName,
    WIZARD_BACK_ACTION,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_CANCEL_ACTION,
    WIZARD_FINISH_ACTION,
    WIZARD_LOADER_START,
    WIZARD_LOADER_STOP,
    WIZARD_META_ACTION,
    WIZARD_META_FAILED,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_ACTION,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_OPEN_MODAL_SUMMARY,
    WIZARD_REPORT_SET_ERRORS,
    WIZARD_REPORT_SET_PARAM,
    WIZARD_REPORT_SET_PARAMS,
    WIZARD_RESTORE_ACTION,
    WIZARD_RESTORE_FAILED,
    WIZARD_RESTORE_SUCCESS,
    WIZARD_SET_ENTITY_ID,
    WIZARD_SET_IS_REQUESTS_STICKY,
    WIZARD_START_ACTION,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
    WIZARD_STORAGE_CLEAR_STEPS,
    WIZARD_STORAGE_CLEAR_STEPS_SUCCESS,
    WIZARD_STORAGE_LOAD_STEPS,
    WIZARD_STORAGE_SAVE_STEP,
    WIZARD_STORAGE_UPDATE_STEP,
} from './wizardConstants';

export interface IWizardMeta {
    wizard: {
        steps: string[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        global?: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    step: IWizardMetaStep;
}

export enum EOrderType {
    RATE_PLAN_ACTIVATE = 'RATE_PLAN_ACTIVATE', // Заказ подключения тарифного плана
    MEGA_POWERS_ACTIVATE = 'MEGA_POWERS_ACTIVATE', // Заказ на подключение МегаСил
    MEGA_POWERS_DEACTIVATE = 'MEGA_POWERS_DEACTIVATE', // Заказ на отключение МегаСил
    SERVICE_ACTIVATE = 'SERVICE_ACTIVATE', // Заказ на подключение услуг
    SERVICE_DEACTIVATE = 'SERVICE_DEACTIVATE', // Заказ на отключение услуг
    CALLS_DETAIL_REPORT = 'CALLS_DETAIL_REPORT', // Заказ детализации вызовов
    LOCK_ACTIVATE = 'LOCK_ACTIVATE', // Заказ на блокировку номера
    LOCK_DEACTIVATE = 'LOCK_DEACTIVATE', // Заказ на разблокировку номера
    CLIENT_EXPENSES_REPORT = 'CLIENT_EXPENSES_REPORT', // Заказ детализация расходов корпоративного клиента
    LABELS_IMPORT = 'LABELS_IMPORT', // Заказ на импорт меток
    MONEY_TRANSFER = 'MONEY_TRANSFER', // Заказ на перевод средств
    ADDITIONAL_ACCOUNTS_ACTIVATE = 'ADDITIONAL_ACCOUNTS_ACTIVATE', // Заказ на подключение дополнительных счетов
    ADDITIONAL_ACCOUNTS_DEACTIVATE = 'ADDITIONAL_ACCOUNTS_DEACTIVATE', // Заказ на отключение дополнительных счетов
    PERSONAL_DATA_IMPORT = 'PERSONAL_DATA_IMPORT', // Заказ на импорт персональных данных
    PERSONAL_DATA_DELETE = 'PERSONAL_DATA_DELETE', // Заказ на удаление персональных данных
    IMPORT_SUBSCRIBERS_LIMITS = 'IMPORT_SUBSCRIBERS_LIMITS', // Заказ на импорт лимитов
}

export interface IWizardOrder {
    orderId: number | string;
    type: EOrderType;
}

export interface IWizardMetaStep {
    isFinal: boolean;
    isFirst: boolean;
    name: TWizardStepNames;

    // Тут может приходить либо единичный заказ, либо несколько заказов (для ProductOfferings)
    // TODO: Сделать в мастерах единую логику заказов (требует переработки ответа от бэка во всех мастерах)
    order?: Pick<IWizardOrder, 'orderId'>;
    orders?: IWizardOrder[];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export type TWizardStepNames =
    | ''
    | ECallsDetailSteps
    | ELabelsImportSteps
    | EMoneyTransferSteps
    | EWizardAdditionalAccountsSteps
    | EWizardBillsDocSteps
    | EWizardChangeLockSteps
    | EWizardExpensesDetailSteps
    | EWizardImportPersonalDataSteps
    | EWizardMultiSearchSteps
    | EWizardOrderTemplateSteps
    | EWizardOrderReportSteps
    | EWizardProductOfferingsSteps
    | EWizardAdditionalReportsSteps
    | EWizardCollationReportSteps
    | EWizardImportSubscribersLimitsSteps
    | EWizardChangeRoleSteps;

/**
 * Start
 */

export interface IWizardStartPayload {
    basketType: EBasketType;
    name: EWizardName;
    data?: Record<string, any>;
}

export interface IWizardStartSuccessPayload {
    basketSnapshotId: string;
    basketSnapshotType: string;
    meta: IWizardMeta;
}

export interface IWizardStartFailedPayload {
    message: string;
}

export type TWizardStartAction = IActionWithOptionalPayload<typeof WIZARD_START_ACTION, IWizardStartPayload>;
export type TWizardStartSuccessAction = IActionWithPayload<typeof WIZARD_START_SUCCESS, IWizardStartSuccessPayload>;
export type TWizardStartFailedAction = IActionWithPayload<typeof WIZARD_START_FAILED, IWizardStartFailedPayload>;

/**
 * Restore
 */

export interface IStorageStepsData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export interface IWizardRestorePayload {
    basketType: EBasketType;
    basketSnapshotId: string;
    basketSnapshotType: string;
    name: EWizardName;
    storageStepsData: IStorageStepsData;
    data?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
}

export interface IWizardRestoreSuccessPayload {
    basketSnapshotId: string;
    basketSnapshotType: string;
    storageStepsData: IStorageStepsData;
}

export interface IWizardRestoreFailedPayload {
    message: string;
}

export type TWizardRestoreAction = IActionWithPayload<typeof WIZARD_RESTORE_ACTION, IWizardRestorePayload>;
export type TWizardRestoreSuccessAction = IActionWithPayload<
    typeof WIZARD_RESTORE_SUCCESS,
    IWizardRestoreSuccessPayload
>;
export type TWizardRestoreFailedAction = IActionWithPayload<typeof WIZARD_RESTORE_FAILED, IWizardRestoreFailedPayload>;

/**
 * Next
 */

export interface IWizardNextSuccessPayload {
    currentStep: IWizardMeta;
    nextStep: IWizardMeta | null;
}

export interface IWizardNextOptions {
    isSticky?: boolean;
}

export interface IWizardNextActionPayload {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;

    options?: IWizardNextOptions;
}

export type TWizardNextAction = IActionWithPayload<typeof WIZARD_NEXT_ACTION, IWizardNextActionPayload>;
export type TWizardNextSuccessAction = IActionWithPayload<typeof WIZARD_NEXT_SUCCESS, IWizardNextSuccessPayload>;
export type TWizardNextFailedAction =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    IActionWithPayload<typeof WIZARD_NEXT_FAILED, any>;

/**
 * Back
 */

export interface IWizardBackSuccessPayload {
    backStep: IWizardMeta;
}

export type TWizardBackAction = IAction<typeof WIZARD_BACK_ACTION>;
export type TWizardBackSuccessAction = IActionWithPayload<typeof WIZARD_BACK_SUCCESS, IWizardBackSuccessPayload>;
export type TWizardBackFailedAction = IActionWithPayload<typeof WIZARD_BACK_FAILED, string>;

/**
 * Meta
 */

export type TWizardMetaAction = IAction<typeof WIZARD_META_ACTION>;
export type TWizardMetaSuccessAction = IActionWithPayload<typeof WIZARD_META_SUCCESS, IWizardMeta>;
export type TWizardMetaFailedAction = IActionWithPayload<typeof WIZARD_META_FAILED, string>;

/**
 * Cancel
 */

export type TWizardCancelAction = IAction<typeof WIZARD_CANCEL_ACTION>;

/**
 * Finish
 */

export type TWizardFinishAction = IAction<typeof WIZARD_FINISH_ACTION>;

/**
 * Loader
 */

export type TWizardLoaderStartAction = IAction<typeof WIZARD_LOADER_START>;
export type TWizardLoaderStopAction = IAction<typeof WIZARD_LOADER_STOP>;

/**
 * Storage
 */

export interface IWizardStorageSaveStepPayload {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any;
}

export interface IWizardStorageUpdateStepPayload {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export interface IWizardStorageClearStepsPayload {
    wizardName: EWizardName;
}

export type TWizardStorageClearStepsAction = IActionWithPayload<
    typeof WIZARD_STORAGE_CLEAR_STEPS,
    IWizardStorageClearStepsPayload
>;
export type TWizardStorageClearStepsSuccess = IAction<typeof WIZARD_STORAGE_CLEAR_STEPS_SUCCESS>;

export type TWizardStorageLoadStepsAction = IAction<typeof WIZARD_STORAGE_LOAD_STEPS>;
export type TWizardStorageSaveStepAction = IActionWithPayload<
    typeof WIZARD_STORAGE_SAVE_STEP,
    IWizardStorageSaveStepPayload
>;
export type TWizardStorageUpdateStepAction = IActionWithPayload<
    typeof WIZARD_STORAGE_UPDATE_STEP,
    IWizardStorageUpdateStepPayload
>;

/**
 * Report
 */

export interface IWizardReportSetParamPayload {
    paramType: EWizardReportParam;
    isEnabled?: boolean;
    isValid?: boolean;
    value?: string;
}

export type TWizardReportSetParamAction = IActionWithPayload<
    typeof WIZARD_REPORT_SET_PARAM,
    IWizardReportSetParamPayload
>;

export interface IWizardReportSetErrorsPayload {
    [EWizardReportParam.EMAIL]?: string;
    [EWizardReportParam.PHONE]?: string;
}

export type TWizardReportSetErrorsAction = IActionWithPayload<
    typeof WIZARD_REPORT_SET_ERRORS,
    IWizardReportSetErrorsPayload
>;

export interface IWizardReportSetParamsPayload {
    [EWizardReportParam.EMAIL]: IWizardReportParam;
    [EWizardReportParam.PHONE]: IWizardReportParam;
    [EWizardReportParam.EMAIL_DEST]: IWizardReportParam;
    [EWizardReportParam.ZIP]: IWizardReportParam;
    [EWizardReportParam.ZIP_PASSWORD]: IWizardReportParam;
}

export type TWizardReportSetParamsAction = IActionWithPayload<
    typeof WIZARD_REPORT_SET_PARAMS,
    IWizardReportSetParamsPayload
>;

/**
 * Wizard sagas map for initial sagas
 */
export const mapWizardNameToSaga: { [key in EWizardName]: () => SagaIterator } = {
    [EWizardName.ADDITIONAL_ACCOUNTS]: wizardAdditionalAccountsSagas,
    [EWizardName.ADDITIONAL_REPORTS]: wizardAdditionalReportsSagas,
    [EWizardName.BILLS_DOC]: wizardBillsDocSagas,
    [EWizardName.CALLS_DETAIL]: callsDetailSagas,
    [EWizardName.CHANGE_LOCK]: wizardChangeLockSagas,
    [EWizardName.EXPENSES_DETAIL]: wizardExpensesDetailSagas,
    [EWizardName.IMPORT_PERSONAL_DATA]: wizardImportPersonalDataSagas,
    [EWizardName.LABELS_IMPORT]: labelsImportSagas,
    [EWizardName.MONEY_TRANSFER]: moneyTransferSagas,
    [EWizardName.MULTI_SEARCH]: wizardMultiSearchSagas,
    [EWizardName.ORDER_TEMPLATE]: wizardOrderTemplateSagas,
    [EWizardName.ORDER_REPORT]: wizardOrderReportSagas,
    [EWizardName.PRODUCT_OFFERINGS]: wizardProductOfferingsSagas,
    [EWizardName.PRODUCT_OFFERINGS_CAMPAIGN]: wizardProductOfferingsSagas,
    [EWizardName.COLLATION_REPORT]: wizardCollationReportSagas,
    [EWizardName.IMPORT_SUBSCRIBERS_LIMITS]: wizardImportSubscribersLimitsSagas,
    [EWizardName.CHANGE_ROLE]: wizardChangeRoleSagas,
};

/**
 * Set current account
 */

export type TWizardSetEntityIdAction = IActionWithPayload<typeof WIZARD_SET_ENTITY_ID, number>;

export interface IWizardValidationError {
    code: string;
    field: string;
}

/**
 * Open modal summary
 */

export interface IWizardOpenModalSummaryPayload {
    orderId?: number | string;
    orderType?: string;
}

export type TWizardOpenModalSummaryAction = IActionWithPayload<
    typeof WIZARD_OPEN_MODAL_SUMMARY,
    IWizardOpenModalSummaryPayload
>;

/**
 * Set flag is requests sticky
 */

export type TWizardSetIsRequestsStickyAction = IActionWithPayload<typeof WIZARD_SET_IS_REQUESTS_STICKY, boolean>;
